/**
 * Playback State Persistence Utility
 * Saves and restores audio playback state for resume functionality
 * Handles phone call interruptions and page visibility changes
 */

const STORAGE_KEY = '__AUDIO_PLAYBACK_STATE__';
const MAX_AGE_MS = 30 * 60 * 1000; // 30 minutes

/**
 * Playback state structure
 * @typedef {Object} PlaybackState
 * @property {string} url - Audio URL being played
 * @property {number} position - Current playback position in seconds
 * @property {string} title - Track title
 * @property {string} artist - Artist name
 * @property {string} cover - Cover image URL
 * @property {number} duration - Track duration in seconds
 * @property {boolean} isLive - Whether it's a live stream
 * @property {number} timestamp - When state was saved (Unix ms)
 */

/**
 * Save current playback state to localStorage
 * @param {PlaybackState} state
 */
export const savePlaybackState = (state) => {
  if (!state || !state.url) return;

  try {
    const stateToSave = {
      ...state,
      timestamp: Date.now()
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
    console.log('[PlaybackStorage] State saved:', stateToSave.title, 'at', stateToSave.position);
  } catch (error) {
    console.error('[PlaybackStorage] Failed to save state:', error);
  }
};

/**
 * Load saved playback state from localStorage
 * Returns null if no state exists or if it's too old
 * @returns {PlaybackState|null}
 */
export const loadPlaybackState = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const state = JSON.parse(stored);

    // Check if state is too old
    const age = Date.now() - state.timestamp;
    if (age > MAX_AGE_MS) {
      console.log('[PlaybackStorage] State too old, clearing');
      clearPlaybackState();
      return null;
    }

    console.log('[PlaybackStorage] Loaded state:', state.title, 'position:', state.position);
    return state;
  } catch (error) {
    console.error('[PlaybackStorage] Failed to load state:', error);
    return null;
  }
};

/**
 * Clear saved playback state
 */
export const clearPlaybackState = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    console.log('[PlaybackStorage] State cleared');
  } catch (error) {
    console.error('[PlaybackStorage] Failed to clear state:', error);
  }
};

/**
 * Check if there's a valid saved state that can be resumed
 * @returns {boolean}
 */
export const hasResumableState = () => {
  const state = loadPlaybackState();
  if (!state) return false;
  // Resume if it was actively playing (live or on-demand)
  if (state.playing) return true;
  // Legacy: resume on-demand with a position
  return !state.isLive && state.position > 0;
};

/**
 * Get age of saved state in human-readable format
 * @returns {string|null}
 */
export const getStateAge = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const state = JSON.parse(stored);
    const ageMs = Date.now() - state.timestamp;
    const ageMin = Math.floor(ageMs / 60000);

    if (ageMin < 1) return 'just now';
    if (ageMin === 1) return '1 minute ago';
    if (ageMin < 60) return `${ageMin} minutes ago`;

    const ageHours = Math.floor(ageMin / 60);
    if (ageHours === 1) return '1 hour ago';
    return `${ageHours} hours ago`;
  } catch {
    return null;
  }
};

export default {
  savePlaybackState,
  loadPlaybackState,
  clearPlaybackState,
  hasResumableState,
  getStateAge,
  STORAGE_KEY,
  MAX_AGE_MS
};
